<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <!-- Toolbar -->
    <div class="cm-toolbar">
      <h2>Daily Operations</h2>

      <!-- Date stepper -->
      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input class="cm-input cm-input-sm" type="date" v-model="dateFrom" @change="fetchData" style="width: 108px; text-align: center;" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input class="cm-input cm-input-sm" type="date" v-model="dateTo" @change="fetchData" style="width: 108px; text-align: center;" />
        <button class="cm-icon-btn" @click="shiftRange(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <!-- Quick range -->
      <div class="cm-seg">
        <button
          v-for="preset in rangePresets"
          :key="preset.label"
          :class="{ on: isActivePreset(preset) }"
          @click="applyPreset(preset)"
        >{{ preset.label }}</button>
      </div>

      <span style="font-size: 11.5px; font-weight: 600;" :style="{ color: isToday ? '#1F9A63' : '#A5A5A6' }">
        {{ rangeLabel }}
      </span>

      <!-- Program filter -->
      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <div class="relative" ref="programDropdown">
          <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="showProgramFilter = !showProgramFilter">
            <FontAwesomeIcon :icon="['fas', 'filter']" style="font-size: 10px;" />
            {{ programFilterLabel }}
          </button>
          <div
            v-if="showProgramFilter"
            class="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg border z-30 py-1"
            style="border-color: #E8E9E9; box-shadow: 0 4px 16px rgba(38,38,40,0.10), 0 1px 3px rgba(38,38,40,0.06);"
          >
            <button
              @click="toggleAllPrograms"
              class="w-full px-3 py-1.5 text-left hover:bg-gray-50"
              style="font-size: 11.5px; font-weight: 500; color: #353537; border: none; background: transparent; cursor: pointer;"
            >{{ allProgramsSelected ? 'Deselect All' : 'Select All' }}</button>
            <div style="border-top: 1px solid #F0F0F1; margin: 4px 0;"></div>
            <div style="max-height: 280px; overflow-y: auto;">
              <label
                v-for="cat in ALL_CASE_CATEGORIES"
                :key="cat"
                class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                style="font-size: 11.5px;"
              >
                <input type="checkbox" :checked="selectedPrograms.includes(cat)" @change="toggleProgram(cat)" />
                <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: CATEGORY_COLORS[cat] || '#6B7280' }"></span>
                <span style="color: #353537;">{{ SHORT_LABELS[cat] || cat }}</span>
              </label>
            </div>
          </div>
        </div>
        <button class="cm-icon-btn" @click="fetchData" :disabled="loading" :title="'Refresh'">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <!-- Summary + Table -->
    <template v-else-if="hasData">
      <!-- Summary stat chips -->
      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 14px 16px;">
        <div class="cm-stat"><span class="cm-stat-label">Roster</span><span class="cm-stat-value">{{ summaryRoster }}</span></div>
        <div class="cm-stat"><span class="cm-stat-label">Due Today</span><span class="cm-stat-value">{{ summaryHopper }}</span></div>
        <div class="cm-stat is-info"><span class="cm-stat-label">New</span><span class="cm-stat-value">{{ summaryNew }}</span></div>
        <div class="cm-stat is-ok"><span class="cm-stat-label">Actioned</span><span class="cm-stat-value">{{ summaryActioned }}</span></div>
        <div class="cm-stat"><span class="cm-stat-label">End of Day</span><span class="cm-stat-value">{{ summaryEnd }}</span></div>
        <div class="cm-stat is-brand"><span class="cm-stat-label">Completion</span><span class="cm-stat-value">{{ summaryRate }}%</span></div>
      </div>

      <!-- Matrix table -->
      <div style="padding: 0 16px 16px;">
        <div class="cm-panel" style="display: inline-block; min-width: 100%;">
          <table class="cm-table" style="border-collapse: separate; border-spacing: 0;">
            <thead>
              <!-- Row 1: Category group headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9; min-width: 156px;"></th>
                <th
                  v-for="cat in filteredCategories"
                  :key="'grp-' + cat.key"
                  :colspan="5"
                  class="ctr"
                  style="border-left: 2px solid #E8E9E9;"
                >
                  <span style="display: inline-flex; align-items: center; gap: 5px;">
                    <span style="width: 7px; height: 7px; border-radius: 50%;" :style="{ background: cat.color }"></span>
                    <span style="color: #353537;">{{ cat.label }}</span>
                  </span>
                </th>
              </tr>
              <!-- Row 2: Metric sub-headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9;">Care Manager</th>
                <template v-for="cat in filteredCategories" :key="'sub-' + cat.key">
                  <th class="num" :style="{ borderLeft: '2px solid #E8E9E9', paddingLeft: '6px', paddingRight: '6px' }" title="Roster"><span style="color: #8B8B8D; font-size: 10px;">R</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="Start of day"><span style="color: #8B8B8D; font-size: 10px;">S</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="New in"><span style="color: #2563EB; font-size: 10px;">+</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="Completed"><span style="color: #1F9A63; font-size: 10px;">✓</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="End of day"><span style="color: #8B8B8D; font-size: 10px;">E</span></th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cm in trackerData.care_managers" :key="cm">
                <td style="position: sticky; left: 0; z-index: 1; border-bottom: 1px solid #F0F0F1;"
                  :style="{
                    background: cm === 'Unassigned' ? '#FDF4E7' : '#FFFFFF',
                  }">
                  <span style="display: inline-flex; align-items: center; gap: 8px;">
                    <span v-if="cm === 'Unassigned'" style="width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; color: #B45309;"><FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" style="font-size: 11px;" /></span>
                    <span v-else class="cm-avatar" :style="avatarStyle(cm)">{{ avatarInitials(cm) }}</span>
                    <span :style="{ fontWeight: 500, color: cm === 'Unassigned' ? '#B45309' : '#353537' }">{{ formatName(cm) }}</span>
                  </span>
                </td>
                <template v-for="cat in filteredCategories" :key="cm + '-' + cat.key">
                  <td v-for="(m, mi) in metrics" :key="cm + cat.key + m.k" class="num"
                    :style="{
                      borderLeft: mi === 0 ? '2px solid #E8E9E9' : undefined,
                      paddingLeft: '6px', paddingRight: '6px',
                      color: cellColor(cell(cm, cat.key, m.k), m.k),
                      fontWeight: (m.k === 'new' || m.k === 'done') ? 600 : 400,
                    }">{{ cell(cm, cat.key, m.k) || '' }}</td>
                </template>
              </tr>

              <!-- Totals row -->
              <tr>
                <td style="position: sticky; left: 0; z-index: 1; background: #F8F8F9; font-weight: 700; color: #353537; border-top: 2px solid #D8D8DA;">Total</td>
                <template v-for="cat in filteredCategories" :key="'tot-' + cat.key">
                  <td v-for="(m, mi) in metrics" :key="'tot' + cat.key + m.k" class="num"
                    :style="{
                      borderLeft: mi === 0 ? '2px solid #E8E9E9' : undefined,
                      paddingLeft: '6px', paddingRight: '6px',
                      background: '#F8F8F9', borderTop: '2px solid #D8D8DA', fontWeight: 700,
                      color: filteredTotal(cat.key, m.k) ? (m.k === 'new' ? '#2563EB' : m.k === 'done' ? '#1F9A63' : '#353537') : '#DBDBDC',
                    }">{{ filteredTotal(cat.key, m.k) || '' }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else class="cm-empty">
      <FontAwesomeIcon :icon="['fas', 'table']" class="ico" />
      <div class="msg">No operations data for this date range</div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// IHCI (tenant 8) programs — exactly IHCI's three CM metrics, no invented
// splits. Category strings must match CM_OPS_TASKS.CASE_CATEGORY for IHCI's
// slugs (see data-engineering .../02_cm_ops_tasks.sql):
//   IHCI_TOC_IP -> 'Post-Discharge IP'
//   IHCI_TOC_ED -> 'Post-Discharge ED'
//   IHCI_SNF_ADMISSION -> 'SNF Admission'
const CATEGORY_COLORS = {
  'Post-Discharge IP': '#DC2626',
  'Post-Discharge ED': '#F59E0B',
  'SNF Admission': '#EC4899', '_total': '#353537',
};
const SHORT_LABELS = {
  'Post-Discharge IP': 'Post-Discharge IP',
  'Post-Discharge ED': 'Post-Discharge ED',
  'SNF Admission': 'SNF Admission', '_total': 'All Programs',
};
const ALL_CASE_CATEGORIES = [
  'Post-Discharge IP',
  'Post-Discharge ED',
  'SNF Admission',
];

export default {
  name: 'CareManagement_Operations_IHCI',
  components: { FontAwesomeIcon },
  created() {
    this.ALL_CASE_CATEGORIES = ALL_CASE_CATEGORIES;
    this.CATEGORY_COLORS = CATEGORY_COLORS;
    this.SHORT_LABELS = SHORT_LABELS;
  },
  data() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      loading: false,
      trackerData: {},
      dateFrom: today,
      dateTo: today,
      showProgramFilter: false,
      metrics: [
        { k: 'roster' }, { k: 'start' }, { k: 'new' }, { k: 'done' }, { k: 'end' },
      ],
      selectedPrograms: (() => {
        try {
          const saved = localStorage.getItem('cm_ops_selected_programs_ihci');
          if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length) return parsed; }
        } catch (e) { /* ignore */ }
        return [...ALL_CASE_CATEGORIES];
      })(),
    };
  },
  computed: {
    summaryRoster() { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.roster || 0), 0); },
    summaryHopper() { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.start || 0), 0); },
    summaryNew() { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.new || 0), 0); },
    summaryActioned() { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.done || 0), 0); },
    summaryEnd() { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.end || 0), 0); },
    summaryRate() { return this.summaryHopper ? Math.round((this.summaryActioned / this.summaryHopper) * 100) : 0; },
    isToday() { const now = new Date(); return this.dateTo === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; },
    isSingleDay() { return this.dateFrom === this.dateTo; },
    rangeLabel() {
      if (this.isSingleDay) {
        if (this.isToday) return 'Today';
        const d = new Date(this.dateTo + 'T12:00:00');
        return `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]} ${d.getMonth()+1}/${d.getDate()}`;
      }
      return `${Math.round((new Date(this.dateTo+'T12:00:00') - new Date(this.dateFrom+'T12:00:00')) / 86400000) + 1} days`;
    },
    rangePresets() {
      const now = new Date();
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const daysAgo = n => { const d = new Date(now); d.setDate(d.getDate() - n); return fmt(d); };
      return [ { label: 'Today', from: fmt(now), to: fmt(now) }, { label: '3d', from: daysAgo(2), to: fmt(now) }, { label: '7d', from: daysAgo(6), to: fmt(now) } ];
    },
    hasData() { return this.trackerData.care_managers?.length > 0; },
    allCategories() {
      const cats = (this.trackerData.case_categories || []).map(c => ({ key: c, label: SHORT_LABELS[c] || c, color: CATEGORY_COLORS[c] || '#6B7280' }));
      cats.push({ key: '_total', label: 'All Programs', color: '#353537' });
      return cats;
    },
    allowedCategories() { return new Set(this.selectedPrograms); },
    filteredCategories() { return this.allCategories.filter(c => c.key === '_total' || this.allowedCategories.has(c.key)); },
    allProgramsSelected() { return this.selectedPrograms.length === ALL_CASE_CATEGORIES.length; },
    programFilterLabel() {
      if (this.allProgramsSelected) return 'All Programs';
      if (this.selectedPrograms.length === 0) return 'No Programs';
      if (this.selectedPrograms.length === 1) return SHORT_LABELS[this.selectedPrograms[0]] || this.selectedPrograms[0];
      return `${this.selectedPrograms.length} Programs`;
    },
  },
  watch: {
    selectedPrograms: { handler(val) { localStorage.setItem('cm_ops_selected_programs_ihci', JSON.stringify(val)); }, deep: true },
  },
  mounted() { this.fetchData(); document.addEventListener('click', this.handleClickOutside); },
  beforeUnmount() { document.removeEventListener('click', this.handleClickOutside); },
  methods: {
    shiftRange(direction) {
      const from = new Date(this.dateFrom + 'T12:00:00'), to = new Date(this.dateTo + 'T12:00:00');
      const span = Math.round((to - from) / 86400000) + 1;
      from.setDate(from.getDate() + direction * span); to.setDate(to.getDate() + direction * span);
      const now = new Date(); now.setHours(23, 59, 59, 999);
      if (to > now) return;
      this.dateFrom = from.toISOString().split('T')[0]; this.dateTo = to.toISOString().split('T')[0];
      this.fetchData();
    },
    applyPreset(preset) { this.dateFrom = preset.from; this.dateTo = preset.to; this.fetchData(); },
    isActivePreset(preset) { return this.dateFrom === preset.from && this.dateTo === preset.to; },
    toggleProgram(key) { const idx = this.selectedPrograms.indexOf(key); if (idx >= 0) this.selectedPrograms.splice(idx, 1); else this.selectedPrograms.push(key); },
    toggleAllPrograms() { this.selectedPrograms = this.allProgramsSelected ? [] : [...ALL_CASE_CATEGORIES]; },
    handleClickOutside(e) { if (this.$refs.programDropdown && !this.$refs.programDropdown.contains(e.target)) this.showProgramFilter = false; },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/operations/', { date_from: this.dateFrom, date_to: this.dateTo });
        this.trackerData = data;
      } catch (e) { console.error('Failed to fetch daily tracker:', e); this.trackerData = {}; }
      finally { this.loading = false; }
    },
    cell(cm, cat, metric) {
      if (cat === '_total') { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.data?.[cm]?.[c.key]?.[metric] || 0), 0) || ''; }
      const v = this.trackerData.data?.[cm]?.[cat]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    filteredTotal(cat, metric) {
      if (cat === '_total') { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.column_totals?.[c.key]?.[metric] || 0), 0) || ''; }
      const v = this.trackerData.column_totals?.[cat]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    cellColor(v, k) { return !v ? '#DBDBDC' : (k === 'new' ? '#2563EB' : k === 'done' ? '#1F9A63' : '#353537'); },
    formatName(name) {
      if (!name) return '—';
      if (name.includes(',')) { const [last, first] = name.split(',').map(s => s.trim()); return `${this.cap(first)} ${this.cap(last)}`; }
      return name;
    },
    cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''; },
    avatarInitials(name) {
      const parts = this.formatName(name).split(' ');
      return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
    },
    avatarStyle(name) {
      const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
      return { color: `hsl(${hue} 42% 34%)`, background: `hsl(${hue} 44% 92%)` };
    },
  },
};
</script>
